import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { useMenuAnclado } from './useMenuAnclado';

/**
 * Desplegable de selección MÚLTIPLE con casillas — el hermano de `CustomSelect` (mismo botón,
 * mismo menú portaleado, `useMenuAnclado`). El menú no se cierra al tocar una opción: se
 * eligen varias y se cierra tocando afuera.
 *
 * `opcionTodas` agrega arriba una fila "Todas…" separada del resto. Qué significa tocarla lo
 * decide quien lo usa (en permisos: "todas" incluye las marcas que se creen después, que no es
 * lo mismo que tildar las que hay hoy).
 */
export interface CustomMultiSelectOption {
  value: string;
  label: string;
}

interface CustomMultiSelectProps {
  options: CustomMultiSelectOption[];
  values: string[];
  onToggle: (value: string) => void;
  /** Lo que dice el botón cerrado ("Todas las marcas", "2 de 3 marcas"…). */
  resumen: string;
  opcionTodas?: { label: string; activa: boolean; onToggle: () => void };
  disabled?: boolean;
  className?: string;
  /** Nombre accesible del botón. */
  etiqueta?: string;
}

function Casilla({ marcada }: { marcada: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`grid h-4 w-4 shrink-0 place-items-center rounded-[4px] border transition-colors ${
        marcada ? 'border-brand-primary bg-brand-primary text-white' : 'border-neutral-300 bg-white'
      }`}
    >
      {marcada && <Check className="h-3 w-3" strokeWidth={3} />}
    </span>
  );
}

export const CustomMultiSelect: React.FC<CustomMultiSelectProps> = ({
  options,
  values,
  onToggle,
  resumen,
  opcionTodas,
  disabled = false,
  className = '',
  etiqueta,
}) => {
  const [open, setOpen] = useState(false);
  const { wrapRef, btnRef, menuRef, pos } = useMenuAnclado(open, setOpen);

  const fila = (marcada: boolean, label: string, onClick: () => void, key: string) => (
    <button
      key={key}
      type="button"
      role="menuitemcheckbox"
      aria-checked={marcada}
      onClick={onClick}
      className="w-full text-left px-3 py-2 text-[15px] flex items-center gap-2.5 text-brand-black/90 hover:bg-neutral-100 transition-colors"
    >
      <Casilla marcada={marcada} />
      <span className="truncate">{label}</span>
    </button>
  );

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={etiqueta}
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={`w-full h-10 flex items-center justify-between px-3 border border-neutral-200 rounded-md text-[15px] bg-surface-card transition-colors ${
          disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-surface-elevated focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary'
        } outline-none`}
      >
        <span className="text-brand-black/90 truncate">{resumen}</span>
        <ChevronDown className={`w-4 h-4 text-neutral-400 shrink-0 ml-2 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="bg-surface-card border border-neutral-200 rounded-md shadow-md py-1 overflow-y-auto"
            style={{
              position: 'fixed',
              left: pos.left,
              width: pos.width,
              top: pos.top,
              bottom: pos.bottom,
              maxHeight: pos.maxHeight,
              zIndex: 100000,
            }}
          >
            {opcionTodas && (
              <>
                {fila(opcionTodas.activa, opcionTodas.label, opcionTodas.onToggle, '__todas')}
                <div className="my-1 border-t border-neutral-200" />
              </>
            )}
            {options.map((o) => fila(values.includes(o.value), o.label, () => onToggle(o.value), o.value))}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default CustomMultiSelect;
